import { useCallback, useEffect, useMemo, useReducer, useRef, useState, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';
import { Agent, AgentsSubscriptionInfo, AuthenticationError, AuthorizationError, DuplicatedError, NetworkError, Subscription } from '@back4app2/sdk';
import { useNavigate, useParams } from 'react-router-dom';
import LoadingMessage from '../components/LoadingMessage';
import ErrorMessage from '../components/ErrorMessage';
import back4app2, { useBack4app2ConnectionStatus } from '../back4app2';
import { BACK4APP_DOT_COM_SITE_URL } from '../settings';
import Button from '../components/Button';
import LoadingSpinner from '../components/LoadingSpinner';
import toast from 'react-hot-toast';
import { ReactComponent as StatusErrorSVG } from '../assets/images/status-error.svg';
import { ReactComponent as StatusSuccessSVG } from '../assets/images/status-success.svg';
import GhostMessage from '../components/GhostMessage';
import { ReactComponent as ErrorIconSVG } from '../assets/images/error-icon.svg';
import { ReactComponent as EnterIconSVG } from '../assets/images/enter-icon.svg';
import { ReactComponent as CloseIconSVG } from '../assets/images/close-icon.svg';
import { ReactComponent as PlusIconSVG } from '../assets/images/plus-icon.svg';
import { ReactComponent as StopIconSVG } from '../assets/images/stop-icon.svg';
import { ReactComponent as ScreenshotIconSVG } from '../assets/images/screenshot-icon.svg';
import { ReactComponent as AttachIconSVG } from '../assets/images/attach-icon.svg';
import MyAgent from './MyAgent';
import { PendingFile, UploadedFile } from '../components/AIAgentComp/AgentInput';
import {
  AGENT_ATTACHMENT_ALLOWED_TYPES,
  AGENT_ATTACHMENT_MAX_FILE_SIZE,
  AGENT_ATTACHMENT_MAX_FILES,
} from '../types/Agent';
import { AmplitudeEvent, trackEvent } from '../utils/amplitude';
import AppPanel from '../components/AppPanel';
import { CreateAppPhase } from '../components/AIAgentComp/CreateAppModal';
import { LinkAppPhase } from '../components/AIAgentComp/LinkAppModal';
import PreviewErrorBoundary from '../components/PreviewErrorBoundary';
import AgentDropdown from '../components/AIAgentComp/AgentDropdown';
import ActionsMenu from '../components/AIAgentComp/ActionsMenu';
import RenameAgentModal from '../components/AIAgentComp/RenameAgentModal';
import DeleteAgentModal from '../components/AIAgentComp/DeleteAgentModal';
import UsageModal from '../components/AIAgentComp/UsageModal';
import UsageCard from '../components/AIAgentComp/UsageCard';
import MobilePreviewUrlBar from '../components/AppPanel/MobilePreviewUrlBar';
import MobileClassSelectorBar from '../components/AppPanel/MobileClassSelectorBar';
import MobileCloudCodeToggle from '../components/AppPanel/MobileCloudCodeToggle';
import { PublicFile, ViewMode, VIEW_MODE_OPTIONS, ParseClass } from '../types/AppPanel';
import { initializePaddle, InitializePaddleOptions } from '@paddle/paddle-js';
import { useIsMobile } from '../hooks/useIsMobile';
import { useAgentView } from '../contexts';
import AgentUpgradeModal from '../components/AIAgentComp/AgentUpgradeModal';
import { MessageAttachment } from '../types/Agent';

// Attachment Preview Modal Component - uses createPortal to render at document.body level
const AttachmentPreviewModal = ({ 
  attachment, 
  onClose 
}: { 
  attachment: MessageAttachment; 
  onClose: () => void;
}) => {
  return createPortal(
    <div 
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/80 backdrop-blur-sm"
      onClick={onClose}
    >
      <div className="relative max-w-[90vw] max-h-[90vh]" onClick={(e) => e.stopPropagation()}>
        <button
          onClick={onClose}
          className="absolute -top-3 -right-3 w-8 h-8 bg-[#1a2332] border border-white/20 rounded-full flex items-center justify-center text-white/70 hover:text-white transition-colors z-10"
          title="Close"
        >
          <CloseIconSVG width="12" height="12" />
        </button>
        <img 
          src={attachment.presignedUrl} 
          alt={attachment.fileName}
          className="max-w-full max-h-[85vh] object-contain rounded-lg"
        />
        <div className="absolute bottom-0 left-0 right-0 bg-black/60 text-white text-xs p-2 rounded-b-lg truncate">
          {attachment.fileName}
        </div>
      </div>
    </div>,
    document.body
  );
};

// const AGENT_PLAN_FEATURES = [
//   'Higher agent message limits',
//   'Faster response priority',
//   'Includes hosting & custom domains',
//   'Priority support',
// ];

// const AGENT_PLAN_PRICE = process.env.REACT_APP_AGENT_PLAN_PRICE_LABEL || '$29';

// Pending file state is shared across views

interface MyAgentsState {
  isFirstLoad: boolean;
  isLoading: boolean;
  loadingErrorMessage?: string;
  agents?: Agent[];  
  isCreating: boolean;
  creatingErrorMessage?: string;
  isLoadingPaddle: boolean;
  paddleError?: string;
  paddle?: any;
  openCheckout: boolean;
  paymentCompleted: boolean;
}

const INITIAL_STATE: MyAgentsState = {
  isFirstLoad: false,
  isLoading: true,
  isCreating: false,
  isLoadingPaddle: false,
  openCheckout: false,
  paymentCompleted: false,
};

enum MyAgentsActionType {
  RESET,
  FINISH_LOADING,
  CREATE,
  RESET_CREATING,
  FINISH_CREATING,
  RESET_CREATING_ERROR_MESSAGE,
  FINISH_UPDATING,
  FINISH_DELETING,
  SET_PADDLE_LOADING,
  FINISH_LOADING_PADDLE,
  SET_CHECKOUT_MODAL,
  SET_PAYMENT_COMPLETED,
}

const reset = () => ({
  type: MyAgentsActionType.RESET
} as const);

const finishLoading = (isFirstLoad: boolean, errorMessage?: string, agents?: Agent[]) => ({
  type: MyAgentsActionType.FINISH_LOADING,
  payload: {
    isFirstLoad,
    errorMessage,
    agents,
  }
} as const);

const resetCreating = () => ({
  type: MyAgentsActionType.RESET_CREATING
} as const);

const create = () => ({
  type: MyAgentsActionType.CREATE
} as const);

const finishCreating = (errorMessage?: string, agent?: Agent) => ({
  type: MyAgentsActionType.FINISH_CREATING,
  payload: {
    errorMessage,
    agent,
  }
} as const);

const resetCreatingErrorMessage = () => ({
  type: MyAgentsActionType.RESET_CREATING_ERROR_MESSAGE
} as const);

const finishUpdating = (agent: Agent) => ({
  type: MyAgentsActionType.FINISH_UPDATING,
  payload: {
    agent,
  }
} as const);

const finishDeleting = (agentId: string) => ({
  type: MyAgentsActionType.FINISH_DELETING,
  payload: {
    agentId,
  }
} as const);

const setCheckoutModal = (isOpen: boolean) => ({
  type: MyAgentsActionType.SET_CHECKOUT_MODAL,
  payload: isOpen,
} as const);

const setPaddleLoading = (isLoading: boolean) => ({
  type: MyAgentsActionType.SET_PADDLE_LOADING,
  payload: isLoading,
} as const);

const finishLoadingPaddle = (errorMessage?: string, paddle?: any) => ({
  type: MyAgentsActionType.FINISH_LOADING_PADDLE,
  payload: {
    errorMessage,
    paddle,
  }
} as const);

const setPaymentCompleted = (isOpen: boolean) => ({
  type: MyAgentsActionType.SET_PAYMENT_COMPLETED,
  payload: isOpen,
} as const);

type MyAgentsAction =
  | ReturnType<typeof reset>
  | ReturnType<typeof finishLoading>
  | ReturnType<typeof create>
  | ReturnType<typeof resetCreating>
  | ReturnType<typeof finishCreating>
  | ReturnType<typeof resetCreatingErrorMessage>
  | ReturnType<typeof finishUpdating>
  | ReturnType<typeof finishDeleting>
  | ReturnType<typeof setPaddleLoading>
  | ReturnType<typeof finishLoadingPaddle>
  | ReturnType<typeof setCheckoutModal>
  | ReturnType<typeof setPaymentCompleted>;

const reducer = (state: MyAgentsState = INITIAL_STATE, action: MyAgentsAction): MyAgentsState => {
  switch (action.type) {
    case MyAgentsActionType.RESET:
      return {
        ...INITIAL_STATE
      };
    
    case MyAgentsActionType.FINISH_LOADING:
      return {
        ...state,
        isFirstLoad: action.payload.isFirstLoad,
        isLoading: false,
        loadingErrorMessage: action.payload.errorMessage,
        agents: (action.payload.agents && [...action.payload.agents]) || undefined
      }

    case MyAgentsActionType.RESET_CREATING:
      return {
        ...state,
        isCreating: false,
        creatingErrorMessage: undefined
      }

    case MyAgentsActionType.CREATE:
      return {
        ...state,
        isCreating: true,
        creatingErrorMessage: undefined
      }

    case MyAgentsActionType.FINISH_CREATING:
      return {
        ...state,
        isCreating: false,
        creatingErrorMessage: action.payload.errorMessage,
        agents: action.payload.agent ? [...((state.agents && state.agents.filter(agent => agent.id !== action.payload.agent!.id)) || []), action.payload.agent].sort((a, b) => a.name.localeCompare(b.name)) : state.agents
      }

    case MyAgentsActionType.RESET_CREATING_ERROR_MESSAGE:
      return {
        ...state,
        creatingErrorMessage: undefined
      }

    case MyAgentsActionType.FINISH_UPDATING:
      return {
        ...state,
        agents: [...((state.agents && state.agents.filter(agent => agent.id !== action.payload.agent.id)) || []), action.payload.agent].sort((a, b) => a.name.localeCompare(b.name))
      }

    case MyAgentsActionType.FINISH_DELETING:
      return {
        ...state,
        agents: (state.agents && state.agents.filter(agent => agent.id !== action.payload.agentId)) || []
      }
    
    case MyAgentsActionType.SET_PADDLE_LOADING:
      return {
        ...state,
        isLoadingPaddle: action.payload
      }

    case MyAgentsActionType.FINISH_LOADING_PADDLE:
      return {
        ...state,
        isLoadingPaddle: false,
        paddleError: action.payload.errorMessage,
        paddle: action.payload.paddle
      }

    case MyAgentsActionType.SET_CHECKOUT_MODAL:
      return {
        ...state,
        openCheckout: action.payload
      }

    case MyAgentsActionType.SET_PAYMENT_COMPLETED:
      return {
        ...state,
        paymentCompleted: action.payload
      }

    default:
      return state;
  }
};

type PaddleCheckoutRequest = {
  container: HTMLDivElement;
  appId: string;
  appName?: string;
  ownerEmail?: string;
};

const MyAgents = () => {
  const { agentId } = useParams();
  const navigate = useNavigate();
  const [state, dispatch] = useReducer(reducer, INITIAL_STATE);
  const { isFirstLoad, isLoading, loadingErrorMessage, agents, isCreating, creatingErrorMessage, isLoadingPaddle, paddleError, paddle, openCheckout } = state;
  const syncSelectedAgentSeqRef = useRef(0);
  const myAgentRef = useRef<{ 
    setInputContent: (content: string) => void;
    sendMessage: (content: string, uploadedFiles?: UploadedFile[]) => Promise<boolean>;
    stopGeneration: () => Promise<boolean>;
    isBusy: boolean;
  }>(null);
  const [associatedId, setAssociatedId] = useState<string | undefined>(undefined);
  // Dashboard ID (UUID) - used for Back4App internal APIs (/parse-app/{id}, /cli/{id})
  const [dashboardId, setDashboardId] = useState<string | undefined>(undefined);
  const [appName, setAppName] = useState<string | undefined>(undefined);
  const [agentName, setAgentName] = useState<string | undefined>(undefined);
  const [publicUrl, setPublicUrl] = useState<string | undefined>(undefined);
  const [backendHref, setBackendHref] = useState<string | undefined>(undefined);
  const [previewUrl, setPreviewUrl] = useState<string | undefined>(undefined);
  const [previewRefreshTick, setPreviewRefreshTick] = useState(0);
  const [createAppModalOpen, setCreateAppModalOpen] = useState(false);
  const [linkAppModalOpen, setLinkAppModalOpen] = useState(false);
  const [createAppPhase, setCreateAppPhase] = useState<CreateAppPhase>('idle');
  const [linkAppPhase, setLinkAppPhase] = useState<LinkAppPhase>('idle');

  // Mark the body as the Agent screen before paint.
  // Used to prevent showing the Zendesk widget (launcher) in the mobile version.
  useLayoutEffect(() => {
    document.body.classList.add('b4a2-agent-screen');
    return () => {
      document.body.classList.remove('b4a2-agent-screen');
    };
  }, []);

  useEffect(() => {
    trackEvent(AmplitudeEvent.AT_AGENT_UI);
  }, []);

  // Reset create app phase when modal closes
  useEffect(() => {
    if (!createAppModalOpen) {
      setCreateAppPhase('idle');
    }
  }, [createAppModalOpen]);

  // Reset link app phase when modal closes
  useEffect(() => {
    if (!linkAppModalOpen) {
      setLinkAppPhase('idle');
    }
  }, [linkAppModalOpen]);

  const [busyAgents, setBusyAgents] = useState<Record<string, boolean>>({});

  // Computed value based on the current agent
  const previewBusy = agentId ? (busyAgents[agentId] ?? false) : false;
  
  // Parse App info for direct API access
  const [parseServerURL, setParseServerURL] = useState<string | undefined>(undefined);
  const [parseAppId, setParseAppId] = useState<string | undefined>(undefined);
  const [parseMasterKey, setParseMasterKey] = useState<string | undefined>(undefined);

  // Modal states for Usage, Rename and Delete
  const [isUsageModalOpen, setIsUsageModalOpen] = useState(false);
  const [isRenameModalOpen, setIsRenameModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isAgentUpgradeModalOpen, setIsAgentUpgradeModalOpen] = useState(false);
  const [selectedAttachment, setSelectedAttachment] = useState<MessageAttachment | null>(null);
  const billingAgentId = useMemo(() => agentId || agents?.[0]?.id, [agentId, agents]);

  const handleAttachmentClick = useCallback((attachment: MessageAttachment) => {
    setSelectedAttachment(attachment);
  }, []);

  const handleAttachmentModalClose = useCallback(() => {
    setSelectedAttachment(null);
  }, []);

  // Get chatId from selected agent for file uploads
  const selectedAgentChatId = useMemo(() => {
    const selectedAgent = agents?.find(a => a.id === agentId);
    return selectedAgent?.mainChat?.id;
  }, [agentId, agents]);

  // Detect if we're on mobile (< 1024px = lg breakpoint)
  // Use centralized mobile detection hook
  const isMobileScreen = useIsMobile();

  // Get agent view context for navbar visibility control
  const agentViewContext = useAgentView();

  // Hide Zendesk widget on mobile Agent screen
  useEffect(() => {
    // Type declaration for Zendesk
    const zE = (window as any).zE;
    
    if (isMobileScreen) {
      // Hide Zendesk widget on mobile
      if (zE) {
        zE('webWidget', 'hide');
      }
    } else {
      // Show Zendesk widget on desktop
      if (zE) {
        zE('webWidget', 'show');
      }
    }

    // Cleanup: show widget when leaving Agent screen
    return () => {
      if (zE) {
        zE('webWidget', 'show');
      }
    };
  }, [isMobileScreen]);

  // When an agent becomes selected (via URL load or dropdown), fetch it via getAgent()
  // to ensure we have the synced current* metadata in the UI without requiring a full refresh.
  useEffect(() => {
    if (!agentId) return;

    const seq = ++syncSelectedAgentSeqRef.current;

    (async () => {
      try {
        const syncedAgent = await back4app2.getAgent(agentId);
        if (seq !== syncSelectedAgentSeqRef.current) return;
        dispatch(finishUpdating(syncedAgent));
      } catch (e) {
        if (seq !== syncSelectedAgentSeqRef.current) return;

        if (e instanceof AuthenticationError || e instanceof AuthorizationError) {
          window.location.replace(
            `${BACK4APP_DOT_COM_SITE_URL}/login?return-url=${encodeURIComponent(
              window.location.href
            )}`
          );
          return;
        }

        // Avoid noisy UX on transient failures; subscription + existing state will keep UI usable.
        if (process.env.REACT_APP_SENTRY_ENV !== 'production') {
          if (e instanceof NetworkError) {
            console.error('[Agent UI] Network error when syncing selected agent', e);
          } else {
            console.error('[Agent UI] Unexpected error when syncing selected agent', e);
          }
        }
      }
    })();
  }, [agentId]);

  // Sync agent screen state with context (for navbar visibility)
  useEffect(() => {
    agentViewContext.setIsAgentScreen(true);
    return () => {
      agentViewContext.setIsAgentScreen(false);
    };
  }, [agentViewContext]);

  // Mobile view toggle state (only used on mobile screens)
  const [mobileView, setMobileView] = useState<'chat' | 'preview'>('chat');

  // Mobile preview URL bar state (synced from AppPanel)
  const [mobilePreviewPublicFiles, setMobilePreviewPublicFiles] = useState<PublicFile[]>([]);
  const [mobilePreviewCurrentPage, setMobilePreviewCurrentPage] = useState<string>('/');
  const [mobilePreviewRefreshSignal, setMobilePreviewRefreshSignal] = useState(0);

  // Mobile publish button state (synced from AppPanel)
  const publishHandlerRef = useRef<(() => void) | null>(null);
  const [mobilePublishDisabled, setMobilePublishDisabled] = useState(true);
  const [mobilePublishLabel, setMobilePublishLabel] = useState('Publish');

  // Mobile actions menu state
  const [isMobileActionsOpen, setIsMobileActionsOpen] = useState(false);
  const mobileActionsButtonRef = useRef<HTMLButtonElement>(null);
  const mobileActionsMenuRef = useRef<HTMLDivElement>(null);

  // Mobile agent selector state
  const [isMobileAgentSelectorOpen, setIsMobileAgentSelectorOpen] = useState(false);
  const mobileAgentSelectorButtonRef = useRef<HTMLButtonElement>(null);
  const mobileAgentSelectorMenuRef = useRef<HTMLDivElement>(null);

  // Mobile view mode selector state (for Preview tab)
  const [mobileAppViewMode, setMobileAppViewMode] = useState<ViewMode>('preview');
  const [isMobileViewModeSelectorOpen, setIsMobileViewModeSelectorOpen] = useState(false);
  const mobileViewModeSelectorButtonRef = useRef<HTMLButtonElement>(null);
  const mobileViewModeSelectorMenuRef = useRef<HTMLDivElement>(null);

  // Mobile database class selector state (synced from AppPanel)
  const [mobileParseClasses, setMobileParseClasses] = useState<ParseClass[]>([]);
  const [mobileSelectedClass, setMobileSelectedClass] = useState<string | null>(null);
  const databaseRefreshHandlerRef = useRef<(() => void) | null>(null);
  const [isDatabaseRefreshing, setIsDatabaseRefreshing] = useState(false);

  // Mobile cloud code state - on mobile we always show Functions view (no toggle)
  const cloudCodeRefreshHandlerRef = useRef<(() => void) | null>(null);
  const [isCloudCodeRefreshing, setIsCloudCodeRefreshing] = useState(false);

  // Sync mobileView with context (for navbar visibility)
  useEffect(() => {
    agentViewContext.setMobileView(mobileView);
  }, [mobileView, agentViewContext]);

  // Sync mobileAppViewMode with context (for navbar visibility)
  useEffect(() => {
    agentViewContext.setMobileAppViewMode(mobileAppViewMode);
  }, [mobileAppViewMode, agentViewContext]);

  // Close mobile actions menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        mobileActionsButtonRef.current &&
        mobileActionsMenuRef.current &&
        !mobileActionsButtonRef.current.contains(event.target as Node) &&
        !mobileActionsMenuRef.current.contains(event.target as Node)
      ) {
        setIsMobileActionsOpen(false);
      }
    };

    if (isMobileActionsOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isMobileActionsOpen]);

  // Close mobile agent selector when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        mobileAgentSelectorButtonRef.current &&
        mobileAgentSelectorMenuRef.current &&
        !mobileAgentSelectorButtonRef.current.contains(event.target as Node) &&
        !mobileAgentSelectorMenuRef.current.contains(event.target as Node)
      ) {
        setIsMobileAgentSelectorOpen(false);
      }
    };

    if (isMobileAgentSelectorOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isMobileAgentSelectorOpen]);

  // Close mobile view mode selector when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        mobileViewModeSelectorButtonRef.current &&
        mobileViewModeSelectorMenuRef.current &&
        !mobileViewModeSelectorButtonRef.current.contains(event.target as Node) &&
        !mobileViewModeSelectorMenuRef.current.contains(event.target as Node)
      ) {
        setIsMobileViewModeSelectorOpen(false);
      }
    };

    if (isMobileViewModeSelectorOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isMobileViewModeSelectorOpen]);

  // Shared input state (keeps desktop + mobile in sync)
  const [agentInputContent, setAgentInputContent] = useState('');
  const [pendingFiles, setPendingFiles] = useState<PendingFile[]>([]);
  const [mobileInputSending, setMobileInputSending] = useState(false);
  const [mobileInputStopping, setMobileInputStopping] = useState(false);
  const mobileInputRef = useRef<HTMLTextAreaElement>(null);
  const mobileStoppingToastId = useRef<string | null>(null);
  
  // Mobile file upload state (shared)
  const [isMobileFileMenuOpen, setIsMobileFileMenuOpen] = useState(false);
  const mobileFileInputRef = useRef<HTMLInputElement>(null);
  const mobileFileMenuRef = useRef<HTMLDivElement>(null);
  const mobileFileButtonRef = useRef<HTMLButtonElement>(null);
  
  // Check if any mobile file is currently uploading
  const isMobileUploading = pendingFiles.some(f => f.status === 'uploading');
  const mobileUploadedFiles = pendingFiles.filter(f => f.status === 'uploaded' && f.uploadedData).map(f => f.uploadedData!);

  // Clear draft input + attachments when switching agents (prevents leaking text/files across agents)
  const prevAgentIdRef = useRef<string | undefined>(undefined);
  useEffect(() => {
    // Only act on actual changes (including from/to undefined)
    const prev = prevAgentIdRef.current;
    if (prev === agentId) return;
    prevAgentIdRef.current = agentId;

    // Always clear the input when switching agents
    setAgentInputContent('');

    // Revoke preview object URLs to avoid memory leaks, then clear pending files
    setPendingFiles((prevFiles) => {
      prevFiles.forEach((f) => {
        try {
          URL.revokeObjectURL(f.previewUrl);
        } catch {
          // ignore
        }
      });
      return [];
    });

    // Close mobile file menu if open (avoid stale UI)
    setIsMobileFileMenuOpen(false);
  }, [agentId]);
  
  // Close mobile file menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        mobileFileMenuRef.current &&
        !mobileFileMenuRef.current.contains(event.target as Node) &&
        mobileFileButtonRef.current &&
        !mobileFileButtonRef.current.contains(event.target as Node)
      ) {
        setIsMobileFileMenuOpen(false);
      }
    };

    if (isMobileFileMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isMobileFileMenuOpen]);
  
  // Upload a single mobile file to S3 and update its state
  const getImageDimensions = (file: File): Promise<{ width: number; height: number } | null> =>
    new Promise((resolve) => {
      const objectUrl = URL.createObjectURL(file);
      const image = new Image();
      image.onload = () => {
        URL.revokeObjectURL(objectUrl);
        resolve({ width: image.naturalWidth, height: image.naturalHeight });
      };
      image.onerror = () => {
        URL.revokeObjectURL(objectUrl);
        resolve(null);
      };
      image.src = objectUrl;
    });

  const uploadMobileFile = useCallback(async (pendingFile: PendingFile) => {
    if (!selectedAgentChatId) {
      // Remove the file if no chat available
      setPendingFiles(prev => {
        const file = prev.find(f => f.id === pendingFile.id);
        if (file) URL.revokeObjectURL(file.previewUrl);
        return prev.filter(f => f.id !== pendingFile.id);
      });
      toast.error('No chat available');
      return;
    }
    try {
      const dimensions = await getImageDimensions(pendingFile.file);
      if (!dimensions) {
        throw new Error('Failed to read image dimensions');
      }
      const { attachmentId, presignedUrl } = await back4app2.requestPresignedUrl(
        selectedAgentChatId,
        pendingFile.file.name,
        pendingFile.file.type,
        pendingFile.file.size,
        dimensions.width,
        dimensions.height
      );

      const response = await fetch(presignedUrl, {
        method: 'PUT',
        headers: {
          'Content-Type': pendingFile.file.type,
        },
        body: pendingFile.file,
      });

      if (!response.ok) {
        throw new Error(`Failed to upload ${pendingFile.file.name}`);
      }

      const uploadedData: UploadedFile = {
        attachmentId,
        fileName: pendingFile.file.name,
        mimeType: pendingFile.file.type,
        sizeBytes: pendingFile.file.size,
      };

      setPendingFiles(prev => prev.map(f =>
        f.id === pendingFile.id
          ? { ...f, status: 'uploaded' as const, uploadedData }
          : f
      ));
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to upload file', {
        className: 'bg-white px-6 py-4 text-dark text-center rounded-none rounded-bl-lg rounded-br-lg shadow-[0_6px_16px_rgba(0,0,0,0.25)] max-w-xs text-sm',
        icon: <StatusErrorSVG width="24px" height="24px" className="animate-bounce-in" />,
        duration: 3000,
      });

      setPendingFiles(prev => {
        const file = prev.find(f => f.id === pendingFile.id);
        if (file) URL.revokeObjectURL(file.previewUrl);
        return prev.filter(f => f.id !== pendingFile.id);
      });
    }
  }, [selectedAgentChatId]);

  // Mobile FREE credits banner (kept outside the input row)
  const [agentsSubscription, setAgentsSubscription] = useState<AgentsSubscriptionInfo | null>(null);
  const [isMobileCreditsBannerDismissed, setIsMobileCreditsBannerDismissed] = useState(false);
  const [isMobileUpgradeHovered, setIsMobileUpgradeHovered] = useState(false);

  const fetchAgentsSubscription = useCallback(async () => {
    try {
      const data = await back4app2.getMyAgentsSubscription();
      setAgentsSubscription(data);
    } catch {
      setAgentsSubscription(null);
    }
  }, []);

  useEffect(() => {
    if (!isMobileScreen) return;
    fetchAgentsSubscription();
  }, [isMobileScreen, fetchAgentsSubscription]);

  const remainingFreeMessagesMobile =
    agentsSubscription?.planType === 'FREE'
      ? Math.max(agentsSubscription.chatMessagesHardLimit - agentsSubscription.chatMessagesUsage, 0)
      : null;

  const showFreeCreditsBannerMobile =
    remainingFreeMessagesMobile !== null && !isMobileCreditsBannerDismissed;
  const scrollToBottomOffsetRem =
    isMobileScreen && mobileView === 'chat'
      ? (showFreeCreditsBannerMobile ? 10.5 : 12)
      : undefined;
  const canTakeScreenshot =
    typeof navigator !== 'undefined' && !!navigator.mediaDevices?.getDisplayMedia;

  const handleMobileUpgradeClick = () => {
    handleOpenAgentUpgradeModal();
  };

  // Add mobile files and start uploading immediately
  const addMobileFilesAndUpload = useCallback((files: File[]) => {
    const newPendingFiles: PendingFile[] = files.map(file => ({
      id: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      file,
      previewUrl: URL.createObjectURL(file),
      status: 'uploading' as const,
    }));

    setPendingFiles(prev => [...prev, ...newPendingFiles]);
    newPendingFiles.forEach(pf => uploadMobileFile(pf));
  }, [uploadMobileFile]);

  const handleMobileFileSelect = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = Array.from(e.target.files || []);
    if (pendingFiles.length + selectedFiles.length > AGENT_ATTACHMENT_MAX_FILES) {
      toast.error(`Maximum ${AGENT_ATTACHMENT_MAX_FILES} files allowed`);
      return;
    }

    const validFiles = selectedFiles.filter(file => {
      if (!AGENT_ATTACHMENT_ALLOWED_TYPES.includes(file.type)) {
        toast.error(`Invalid file type: ${file.name}`);
        return false;
      }
      if (file.size > AGENT_ATTACHMENT_MAX_FILE_SIZE) {
        toast.error(`File too large: ${file.name}`);
        return false;
      }
      return true;
    });

    if (validFiles.length > 0) {
      addMobileFilesAndUpload(validFiles);
    }
    setIsMobileFileMenuOpen(false);
    if (mobileFileInputRef.current) mobileFileInputRef.current.value = '';
  }, [pendingFiles.length, addMobileFilesAndUpload]);
  
  const handleMobileScreenshot = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getDisplayMedia({ 
        video: true,
        audio: false 
      });
      
      const video = document.createElement('video');
      video.srcObject = stream;
      await video.play();

      const canvas = document.createElement('canvas');
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      
      const ctx = canvas.getContext('2d');
      if (ctx) {
        ctx.drawImage(video, 0, 0);
        canvas.toBlob((blob) => {
          if (blob) {
            const file = new File([blob], `screenshot-${Date.now()}.png`, { type: 'image/png' });
            if (file.size > AGENT_ATTACHMENT_MAX_FILE_SIZE) {
              toast.error('Screenshot too large');
            } else if (pendingFiles.length >= AGENT_ATTACHMENT_MAX_FILES) {
              toast.error(`Maximum ${AGENT_ATTACHMENT_MAX_FILES} files allowed`);
            } else {
              addMobileFilesAndUpload([file]);
            }
          }
          stream.getTracks().forEach(track => track.stop());
        }, 'image/png');
      }
      setIsMobileFileMenuOpen(false);
    } catch (err) {
      console.error('Error taking screenshot:', err);
    }
  }, [pendingFiles.length, addMobileFilesAndUpload]);

  const handleMobilePaste = useCallback((e: React.ClipboardEvent<HTMLTextAreaElement>) => {
    const items = e.clipboardData?.items;
    if (!items) return;

    // Find any file item in clipboard
    let pastedFile: File | null = null;
    for (let i = 0; i < items.length; i += 1) {
      const item = items[i];
      if (item.kind === 'file') {
        pastedFile = item.getAsFile();
        break;
      }
    }

    // No file pasted, let default behavior handle text paste
    if (!pastedFile) return;

    // Prevent default since we're handling the file
    e.preventDefault();

    // Validate file type
    if (!AGENT_ATTACHMENT_ALLOWED_TYPES.includes(pastedFile.type)) {
      toast.error('Unsupported file type. Only PNG, JPEG, WebP, and GIF images are allowed.', {
        className: 'bg-white px-6 py-4 text-dark text-center rounded-none rounded-bl-lg rounded-br-lg shadow-[0_6px_16px_rgba(0,0,0,0.25)] max-w-xs text-sm',
        icon: <StatusErrorSVG width="24px" height="24px" className="animate-bounce-in" />,
        duration: 3000,
      });
      return;
    }

    // Validate file size
    if (pastedFile.size > AGENT_ATTACHMENT_MAX_FILE_SIZE) {
      toast.error('Image is too large (max 20MB)', {
        className: 'bg-white px-6 py-4 text-dark text-center rounded-none rounded-bl-lg rounded-br-lg shadow-[0_6px_16px_rgba(0,0,0,0.25)] max-w-xs text-sm',
        icon: <StatusErrorSVG width="24px" height="24px" className="animate-bounce-in" />,
        duration: 3000,
      });
      return;
    }

    // Validate max files limit
    if (pendingFiles.length >= AGENT_ATTACHMENT_MAX_FILES) {
      toast.error(`You can only attach up to ${AGENT_ATTACHMENT_MAX_FILES} files`, {
        className: 'bg-white px-6 py-4 text-dark text-center rounded-none rounded-bl-lg rounded-br-lg shadow-[0_6px_16px_rgba(0,0,0,0.25)] max-w-xs text-sm',
        icon: <StatusErrorSVG width="24px" height="24px" className="animate-bounce-in" />,
        duration: 3000,
      });
      return;
    }
    addMobileFilesAndUpload([pastedFile]);
  }, [addMobileFilesAndUpload, pendingFiles.length]);
  
  const removeMobileFile = useCallback(async (fileId: string) => {
    const file = pendingFiles.find(f => f.id === fileId);
    if (!file) return;

    // Remove from UI immediately
    setPendingFiles(prev => prev.filter(f => f.id !== fileId));
    URL.revokeObjectURL(file.previewUrl);

    // If the file was uploaded, delete it from S3/DB
    if (file.status === 'uploaded' && file.uploadedData?.attachmentId) {
      try {
        await back4app2.deleteAttachment(file.uploadedData.attachmentId);
      } catch (err) {
        // Log but don't show error to user - file is already removed from UI
        console.error('[MyAgents] Failed to delete mobile attachment:', err);
      }
    }
  }, [pendingFiles]);

  const handleSendRequest = useCallback(
    async (
      content: string,
      uploadedFiles: UploadedFile[] = [],
      setSending?: (value: boolean) => void
    ): Promise<boolean> => {
      if (!myAgentRef.current) return false;
      if ((!content && uploadedFiles.length === 0) || isMobileUploading) return false;

      const contentSnapshot = content;
      const pendingFilesSnapshot = [...pendingFiles];

      setAgentInputContent('');
      setPendingFiles([]);
      if (setSending) setSending(true);

      try {
        const success = await myAgentRef.current.sendMessage(content, uploadedFiles);
        if (!success) {
          setAgentInputContent(contentSnapshot);
          setPendingFiles(pendingFilesSnapshot);
          if (setSending) setSending(false);
          return false;
        }
        pendingFilesSnapshot.forEach(f => URL.revokeObjectURL(f.previewUrl));
        await fetchAgentsSubscription();
        return true;
      } catch (err) {
        console.error('Error sending message:', err);
        toast.error(err instanceof Error ? err.message : 'Failed to send message', {
          className: 'bg-white px-6 py-4 text-dark text-center rounded-none rounded-bl-lg rounded-br-lg shadow-[0_6px_16px_rgba(0,0,0,0.25)] max-w-xs text-sm',
          icon: <StatusErrorSVG width="24px" height="24px" className="animate-bounce-in" />,
          duration: 3000,
        });
        setAgentInputContent(contentSnapshot);
        setPendingFiles(pendingFilesSnapshot);
        return false;
      } finally {
        if (setSending) {
          setSending(false);
        }
      }
    },
    [fetchAgentsSubscription, isMobileUploading, pendingFiles]
  );

  // Mobile input send handler (uses shared handler)
  const handleMobileSend = useCallback(async () => {
    if ((!agentInputContent && mobileUploadedFiles.length === 0) || mobileInputSending || isMobileUploading) return;
    await handleSendRequest(agentInputContent, mobileUploadedFiles, setMobileInputSending);
    mobileInputRef.current?.focus();
  }, [agentInputContent, handleSendRequest, isMobileUploading, mobileInputSending, mobileUploadedFiles]);

  // Mobile input stop handler
  const handleMobileStop = useCallback(async () => {
    if (mobileInputStopping || !myAgentRef.current) return;
    
    setMobileInputStopping(true);
    
    // Show stopping toast
    mobileStoppingToastId.current = toast.loading('Stopping generation...', {
      className: 'bg-white px-6 py-4 text-dark text-center rounded-none rounded-bl-lg rounded-br-lg shadow-[0_6px_16px_rgba(0,0,0,0.25)] max-w-xs text-sm',
    });
    
    try {
      await myAgentRef.current.stopGeneration();
    } catch (e) {
      // Show error toast
      const errorMessage = e instanceof Error ? e.message : 'Failed to stop generation';
      toast.error(errorMessage, {
        className: 'bg-white px-6 py-4 text-dark text-center rounded-none rounded-bl-lg rounded-br-lg shadow-[0_6px_16px_rgba(0,0,0,0.25)] max-w-xs text-sm',
        duration: 3000,
      });
    } finally {
      // Dismiss the stopping toast
      if (mobileStoppingToastId.current) {
        toast.dismiss(mobileStoppingToastId.current);
        mobileStoppingToastId.current = null;
      }
      setMobileInputStopping(false);
    }
  }, [mobileInputStopping]);

  const loadPaddle = useCallback(() => {
    if (paddle) {
      return;
    }

    dispatch(setPaddleLoading(true));

    const paddleOptions = {
      token: process.env.REACT_APP_PADDLE_TOKEN || 'test_0270ab179b4f4abd7aa228c7014',
      environment: process.env.REACT_APP_SENTRY_ENV === 'production' ? 'production' : 'sandbox',
      pwCustomer: {}
    };

    const paddleEventCallback = async (data: any) => {
      if (data.name === 'checkout.completed') {
        const paymentData = {
          appId: data.data.custom_data.appId,
          customerId: data.data.customer.id,
          planName: 'MVP Plan',
          transactionId: data.data.transaction_id,
          checkoutId: data.data.id,
          results: data.data,
          planId: data.data.custom_data.planId,
          email: data.data.customer.email,
        };

        try {
          const response = await fetch(`${process.env.REACT_APP_BACK4APP_CHECKOUT_URL}/save-subscription`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify(paymentData),
          });

          if (!response.ok) {
            throw new Error('Failed to persist subscription in Back4App');
          }

          trackEvent(AmplitudeEvent.AGENT_APP_PUBLISH_PAY_SUCCESS, {
            appId: paymentData.appId,
            planId: paymentData.planId,
            transactionId: paymentData.transactionId,
            checkoutId: paymentData.checkoutId,
          });

          dispatch(setPaymentCompleted(true));
          await new Promise(resolve => setTimeout(resolve, 1000));
          toast.success('Plan updated successfully!', {
            className:"bg-white px-6 py-4 text-dark text-center rounded-none rounded-bl-lg rounded-br-lg shadow-[0_6px_16px_rgba(0,0,0,0.25)] max-w-xs text-sm",
            icon: <StatusSuccessSVG width="24px" height="24px" className="animate-bounce-in" />,
            duration: 6000
          });
          dispatch(setCheckoutModal(false));
        } catch (error) {
          console.error('Error saving subscription:', error);
          dispatch(setPaymentCompleted(false));
          toast.error('Error updating your plan, please contact support!', {
            className:"bg-white px-6 py-4 text-dark text-center rounded-none rounded-bl-lg rounded-br-lg shadow-[0_6px_16px_rgba(0,0,0,0.25)] max-w-xs text-sm",
            icon: <StatusErrorSVG width="24px" height="24px" className="animate-bounce-in" />,
            duration: 6000
          });
          dispatch(setCheckoutModal(false));
          return;
        }
      }

      if (data.name === 'checkout.closed') {
        dispatch(setCheckoutModal(false));
      }
    };

    initializePaddle({ ...paddleOptions, eventCallback: paddleEventCallback } as InitializePaddleOptions)
      .then((paddleInstance: any) => {
        if (paddleInstance) {
          dispatch(finishLoadingPaddle(undefined, paddleInstance));
        }
      })
      .catch((error) => {
        console.error('Paddle initialization failed:', error);
        dispatch(finishLoadingPaddle('Failed to initialize Paddle', undefined));
        toast.error('Failed to open checkout, please contact support!', {
          className:"bg-white px-6 py-4 text-dark text-center rounded-none rounded-bl-lg rounded-br-lg shadow-[0_6px_16px_rgba(0,0,0,0.25)] max-w-xs text-sm",
          icon: <StatusErrorSVG width="24px" height="24px" className="animate-bounce-in" />,
          duration: 6000
        });
      });
  }, [dispatch, paddle]);

  useEffect(() => {
    loadPaddle();
  }, [loadPaddle]);

  const handleOpenPaddleCheckout = useCallback(
    ({ appId, appName, ownerEmail }: PaddleCheckoutRequest) => {
      dispatch(setPaymentCompleted(false));
      // dispatch(setPaddleError(undefined));
      if (!paddle) {
        toast.error('Checkout unavailable. Please try again shortly.', {
          className:"bg-white px-6 py-4 text-dark text-center rounded-none rounded-bl-lg rounded-br-lg shadow-[0_6px_16px_rgba(0,0,0,0.25)] max-w-xs text-sm",
          icon: <StatusErrorSVG width="24px" height="24px" className="animate-bounce-in" />,
          duration: 6000
        });
        return;
      }

      if (!appId) {
        toast.error('Missing application information. Please try again.', {
          className:"bg-white px-6 py-4 text-dark text-center rounded-none rounded-bl-lg rounded-br-lg shadow-[0_6px_16px_rgba(0,0,0,0.25)] max-w-xs text-sm",
          icon: <StatusErrorSVG width="24px" height="24px" className="animate-bounce-in" />,
          duration: 6000
        });
        return;
      }

      if (!ownerEmail) {
        toast.error('Missing owner app email. Please try again.', {
          className:"bg-white px-6 py-4 text-dark text-center rounded-none rounded-bl-lg rounded-br-lg shadow-[0_6px_16px_rgba(0,0,0,0.25)] max-w-xs text-sm",
          icon: <StatusErrorSVG width="24px" height="24px" className="animate-bounce-in" />,
          duration: 6000
        });
        return;
      }

      const paddlePriceId =  process.env.REACT_APP_SENTRY_ENV === 'production' ? 'pri_01jjyr3kxmsav875y1v2p8h68k' : 'pri_01jjykwj65y5de1vcv5xaryw8g';

      const baseCheckoutOptions: Record<string, any> = {
        items: [{ priceId: paddlePriceId, quantity: 1 }],
        title: appName,
        customData: { appId },
        allowLogout: false,
      };

      if (ownerEmail) {
        baseCheckoutOptions.customer = { email: ownerEmail };
      }

      dispatch(setCheckoutModal(true));

      setTimeout(() => {
        
        try {
          paddle.Checkout.open({
            ...baseCheckoutOptions,
            settings: {
              displayMode: 'inline',
              theme: 'light',
              locale: 'en',
              variant: 'one-page',
              allowedPaymentMethods: ['card', 'paypal'],
              frameTarget: 'checkout-container',
              frameInitialHeight: '450',
              frameStyle: `
                width: 100%; 
                min-width: 312px; 
                max-height: 80vh; 
                background-color: #f9f9f9; 
                border: none;
              `
            },
            allowLogout: false,
            customData: { appId: appId, planId: 'gXGhzlMHZ6' },
            customer: {
              email: ownerEmail,
            }
          });

        } catch (error) {
          console.error('Error opening Paddle checkout inline:', error);
          try {
            paddle.Checkout.open(baseCheckoutOptions);
          } catch (fallbackError) {
            console.error('Fallback Paddle checkout also failed:', fallbackError);
            dispatch(setCheckoutModal(false));
            toast.error('Failed to open checkout, please contact support!', {
              className:"bg-white px-6 py-4 text-dark text-center rounded-none rounded-bl-lg rounded-br-lg shadow-[0_6px_16px_rgba(0,0,0,0.25)] max-w-xs text-sm",
              icon: <StatusErrorSVG width="24px" height="24px" className="animate-bounce-in" />,
              duration: 6000
            });
          }
        }
      }, 200);
    },
    [dispatch, paddle]
  );

  const handleClosePaddleCheckout = useCallback(() => {
    if (!paddle) {
      dispatch(setCheckoutModal(false));
      return;
    }

    try {
      paddle.Checkout.close();
    } catch (error) {
      console.error('Error closing Paddle checkout:', error);
    } finally {
      dispatch(setCheckoutModal(false));
    }
  }, [dispatch, paddle]);

  const [processingAgentPlanId, setProcessingAgentPlanId] = useState<string | undefined>(undefined);
  // const agentUpgradeFrameId = 'agent-upgrade-checkout';

  const handleAgentUpgradeClose = useCallback(() => {
    setIsAgentUpgradeModalOpen(false);
    setProcessingAgentPlanId(undefined);
    dispatch(resetCreatingErrorMessage());
  }, [dispatch]);

  const handleAgentUpgradeNow = useCallback((priceId: string) => {
    // handled inside AgentUpgradeModal; keep for compatibility
    setProcessingAgentPlanId(undefined);
  }, []);

  // Helper to get the app id from the public url (legacy - gets last segment)
  const getAppIdFromUrl = (url: string) => {
    const parts = url.split('/');
    return parts.pop() || parts.pop() || undefined;
  }

  /**
   * Extracts the Dashboard ID (UUID) from a Back4App dashboard URL.
   * URLs can be in formats like:
   * - https://dashboard.back4app.com/apps/f7b511cd-f975-413f-af73-32b56ee82f05/browser
   * - https://backend-homolog.back4app.com/apps/f7b511cd-f975-413f-af73-32b56ee82f05/overview
   * - https://dashboard-homolog.back4app.com/parse-app/f7b511cd-f975-413f-af73-32b56ee82f05/cloud
   */
  const extractDashboardIdFromUrl = (url?: string): string | undefined => {
    if (!url) return undefined;
    // Match UUID pattern after /apps/ or /parse-app/
    const match = url.match(/\/(?:apps|parse-app)\/([a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12})/i);
    return match?.[1];
  }

  // Helpers to keep DB as the source-of-truth and avoid clobbering fallback values
  const normalize = useCallback((s?: string) => (s ? (/^https?:\/\//i.test(s) ? s : `https://${s}`) : undefined), []);
  const extractAgentPreviewMeta = useCallback((a?: any) => {

    if (!a) {
      return {
        dashboardId: undefined as string | undefined,
        parseAppIdFromAgent: undefined as string | undefined,
        previewUrl: undefined as string | undefined,
        appName: undefined as string | undefined,
        agentName: undefined as string | undefined,
        backendHref: undefined as string | undefined,
        publicUrl: undefined as string | undefined,
      };
    }

    const publicUrl =
      a.currentPublicWebHostingURL && a.currentPublicWebHostingURL !== 'Public web hosting not activated'
        ? normalize(a.currentPublicWebHostingURL as string)
        : undefined;
    
    // Extract Dashboard ID (UUID) from the dashboard URL
    // This is the correct ID for Back4App internal APIs (/parse-app/{id}, /cli/{id})
    const dashboardUrl = a.currentAppDashboardURL as string | undefined;
    const dashboardId = extractDashboardIdFromUrl(dashboardUrl);
    
    // The currentAppId field contains the Parse Application ID
    // This is used for Paddle checkout and other Parse-specific operations
    const parseAppIdFromAgent = (a.currentAppId as string) || undefined;
    
    return {
      dashboardId,
      parseAppIdFromAgent,
      previewUrl: (a.currentPreviewWebHostingURL as string) || undefined,
      appName: (a.currentAppName as string) || undefined,
      agentName: (a.name as string) || undefined,
      backendHref: dashboardUrl || undefined,
      publicUrl,
    };
  }, [normalize]);

  useEffect(() => {

    if (!agents || !agentId) {
      setAssociatedId(undefined);
      setDashboardId(undefined);
      setAgentName(undefined);
      setPreviewUrl(undefined);
      setAppName(undefined);
      setBackendHref(undefined); 
      setPublicUrl(undefined);
      return;
    }
    const a = agents.find((ag) => ag.id === agentId) as any;
    const meta = extractAgentPreviewMeta(a);
    
    // Set Dashboard ID (UUID) for Back4App internal APIs
    setDashboardId(meta.dashboardId);
    setPreviewUrl(meta.previewUrl);
    
    // Only override fallback if DB provides values
    if (meta.backendHref) {
      const appId = extractDashboardIdFromUrl(meta.backendHref);
      setAssociatedId(appId); 
    } else {
      setAssociatedId(undefined);
    }
    setAppName(meta.appName);
    setAgentName(meta.agentName);
    setBackendHref(meta.backendHref);
    setPublicUrl(meta.publicUrl);
  }, [agents, agentId, extractAgentPreviewMeta]);

  // Fetch Parse App info (appId, masterKey, parseServerURL) when dashboardId changes
  // Note: getParseAppInfo expects the Dashboard ID (UUID) to fetch Parse App details
  useEffect(() => {
    if (!dashboardId) {
      setParseServerURL(undefined);
      setParseAppId(undefined);
      setParseMasterKey(undefined);
      return;
    }

    const fetchParseAppInfo = async () => {
      try {
        const parseAppInfo = await back4app2.getParseAppInfo(dashboardId);
        // dashboardAPI is the Parse Server URL (e.g., https://parseapi.back4app.com)
        setParseServerURL(parseAppInfo.dashboardAPI);
        // appId is the Parse Application ID (X-Parse-Application-Id)
        // This is used for Paddle checkout and Parse REST API calls
        setParseAppId(parseAppInfo.appId);
        setParseMasterKey(parseAppInfo.masterKey);
      } catch (error) {
        console.error('Error fetching Parse App info:', error);
        setParseServerURL(undefined);
        setParseAppId(undefined);
        setParseMasterKey(undefined);
      }
    };

    fetchParseAppInfo();
  }, [dashboardId]);

  // No fallbacks: DB is the only source of truth

  const handleCreate = useCallback(async () => {
    // New flow: send user to the dedicated Create Agent screen.
    // This keeps the old Agents list page clean and allows users to pick how (or if) they want to link a workspace.
    navigate('/agents/new');
  }, [navigate]);

  useEffect(
    () => {
      dispatch(resetCreatingErrorMessage());
    },
    [agentId]
  );

  useEffect(
    () => {
      if (agentId && agents && !agents.find(agent => agent.id === agentId)) {
        navigate('/agents');
      }
    },
    [agentId, agents, navigate]
  );

  useEffect(
    () => {
      if (creatingErrorMessage && agentId) {
        toast.error(creatingErrorMessage, {
          className:"bg-white px-6 py-4 text-dark text-center rounded-none rounded-bl-lg rounded-br-lg shadow-[0_6px_16px_rgba(0,0,0,0.25)] max-w-xs text-sm",
          icon: <StatusErrorSVG width="24px" height="24px" className="animate-bounce-in" />,
          duration: 6000
        });

        dispatch(resetCreatingErrorMessage());
      }
    },
    [creatingErrorMessage, agentId]
  );
  
  useEffect(() => {
    if (creatingErrorMessage === 'Please subscribe to a paying plan in order to create a new Agent.') {
      handleOpenAgentUpgradeModal();
    }
  }, [creatingErrorMessage]); // handleOpenAgentUpgradeModal defined later; safe without dependency
  

  const { isConnected } = useBack4app2ConnectionStatus();

  const agentsRef = useRef<Agent[] | undefined>(undefined);
  useEffect(() => {
    agentsRef.current = agents;
  }, [agents]);

  const hasLoadedAgentsOnceRef = useRef(false);

  const loadAgentsHttp = useCallback(async () => {
    try {
      const snapshot = await back4app2.findAgents();

      const isFirstLoad = !hasLoadedAgentsOnceRef.current;
      hasLoadedAgentsOnceRef.current = true;

      dispatch(finishLoading(isFirstLoad, undefined, snapshot));
    } catch (e) {
      // auth -> login
      if (e instanceof AuthenticationError || e instanceof AuthorizationError) {
        window.location.replace(
          `${BACK4APP_DOT_COM_SITE_URL}/login?return-url=${encodeURIComponent(window.location.href)}`
        );
        return;
      }

      // network: keep loading (no infinite error UI), will retry on reconnect event
      if (e instanceof NetworkError) {
        console.error('[Agent UI] Network error when loading agents (http-first)', e);
        return;
      }

      console.error('[Agent UI] Unexpected error when loading agents (http-first)', e);
      dispatch(
        finishLoading(!hasLoadedAgentsOnceRef.current, 'Unexpected error when loading agents. Please try again.')
      );
    }
  }, [dispatch]);


  // HTTP-first: try immediately
  useEffect(() => {
    loadAgentsHttp();
  }, [loadAgentsHttp]);

  // Event-driven retry: when WS reports connected again, try HTTP once
  useEffect(() => {
    if (isConnected && !hasLoadedAgentsOnceRef.current) {
      loadAgentsHttp();
    }
  }, [isConnected, loadAgentsHttp]);

  // Keep WS subscription for live updates (do NOT block initial render)
  useEffect(() => {
    const subscription = back4app2.subscribeToAgents((error, snapshot) => {
      if (error) {
        if (error instanceof AuthenticationError || error instanceof AuthorizationError) {
          window.location.replace(
            `${BACK4APP_DOT_COM_SITE_URL}/login?return-url=${encodeURIComponent(window.location.href)}`
          );
          return;
        }

        // If we already have data, don't break UI
        if (agentsRef.current && agentsRef.current.length) {
          console.error('[Agent UI] Subscription error (ignored; UI already has agents)', error);
          return;
        }

        // No data yet: keep loading; HTTP effect will try again on reconnect
        if (error instanceof NetworkError) {
          console.error('[Agent UI] Subscription network error while loading agents', error);
          return;
        }

        console.error('[Agent UI] Subscription unexpected error while loading agents', error);
        dispatch(finishLoading(true, 'Unexpected error when loading agents. Please try again.'));
        return;
      }

      hasLoadedAgentsOnceRef.current = true;
      dispatch(finishLoading(false, undefined, snapshot));
    });

    return () => {
      subscription.unsubscribe();
      dispatch(reset());
    };
  }, [dispatch]);


  useEffect(
    () => {
      if (!agentId && !isLoading && !isCreating && !creatingErrorMessage) {
        if (agents && agents.length > 0) {
          navigate(`/agents/${agents[0].id}`);
        } else {
          // Only auto-create on real first login (server-side flag)
          (async () => {
            try {
              const { isFirstLogin } = await back4app2.getOnBoardingQuestions();
              if (isFirstLogin) {
                handleCreate();
              }
            } catch (e) {
              // ignore and keep empty state
              console.debug('getOnBoardingQuestions error', e);
            }
          })();
        }
      }
    },
    [
      agentId,
      isLoading,
      agents,
      isCreating,
      creatingErrorMessage,
      navigate,
      handleCreate
    ]
  )

  const handleCreateButtonClick = () => {
    navigate('/agents');
    trackEvent(AmplitudeEvent.AGENT_CREATE_ACTION);
    handleCreate();
  };

  const handleOpenAgentUpgradeModal = useCallback(() => {
    if (!billingAgentId) {
      toast.error('Please select or create an agent before upgrading.', {
        className:"bg-white px-6 py-4 text-dark text-center rounded-none rounded-bl-lg rounded-br-lg shadow-[0_6px_16px_rgba(0,0,0,0.25)] max-w-xs text-sm",
        icon: <StatusErrorSVG width="24px" height="24px" className="animate-bounce-in" />,
        duration: 5000
      });
      return;
    }
    setIsAgentUpgradeModalOpen(true);
  }, [billingAgentId]);

  const handleUpdate = async (agentId: string, name: string) => {
    let agent;

    try {
      agent = await back4app2.updateAgent(agentId, name);
    } catch (e) {
      let errorMessage;

      if (e instanceof NetworkError) {
        console.error('network error', e);
        errorMessage = 'Network error when updating agent. Check your internet connection.';
      } else if (e instanceof AuthenticationError || e instanceof AuthorizationError) {
        window.location.replace(`${BACK4APP_DOT_COM_SITE_URL}/login?return-url=${encodeURIComponent(window.location.href)}`);
        return;
      } else if (e instanceof DuplicatedError) {
        errorMessage = 'You already have an agent with this same name.';
      } else {
        console.error('unexpected error updating agent', e);
        errorMessage = 'Unexpected error when updating agent.';
      }

      toast.error(errorMessage as string, {
        className:"bg-white px-6 py-4 text-dark text-center rounded-none rounded-bl-lg rounded-br-lg shadow-[0_6px_16px_rgba(0,0,0,0.25)] max-w-xs text-sm",
        icon: <StatusErrorSVG width="24px" height="24px" className="animate-bounce-in" />,
        duration: 6000
      });

      return;
    }

    dispatch(finishUpdating(agent));
  };

  const handleDelete = async (agentId: string) => {
    try {
      await back4app2.deleteAgent(agentId);
    } catch (e) {
      let errorMessage;

      if (e instanceof NetworkError) {
        console.error('network error', e);
        errorMessage = 'Network error when deleting agent. Check your internet connection.';
      } else if (e instanceof AuthenticationError || e instanceof AuthorizationError) {
        window.location.replace(`${BACK4APP_DOT_COM_SITE_URL}/login?return-url=${encodeURIComponent(window.location.href)}`);
        return;
      } else {
        console.error('unexpected error deleting agent', e);
        errorMessage = 'Unexpected error when deleting agent.';
      }

      toast.error(errorMessage as string, {
        className:"bg-white px-6 py-4 text-dark text-center rounded-none rounded-bl-lg rounded-br-lg shadow-[0_6px_16px_rgba(0,0,0,0.25)] max-w-xs text-sm",
        icon: <StatusErrorSVG width="24px" height="24px" className="animate-bounce-in" />,
        duration: 6000
      });

      return;
    }

    dispatch(finishDeleting(agentId));
  };

  if (isLoading || (!agentId && !isCreating && !creatingErrorMessage && isFirstLoad)) {
    return (
      <LoadingMessage message="Loading agents..." />
    );
  }

  if (loadingErrorMessage) {
    return (
      <ErrorMessage message={loadingErrorMessage} />
    );
  }

  return (
    <>
    <div className={`flex flex-col z-[1] gap-0 lg:gap-4 p-0 lg:p-4 lg:pb-4 ${mobileView === 'chat' ? 'h-[calc(100vh-80px)]' : 'h-[100vh]'} lg:h-[calc(100vh-80px)] lg:pb-4`}>
      {/* Left/Right content */}
      <div className="flex grow gap-4 min-h-0">
        {/* Left: Agent section (30% on desktop, full width on mobile) */}
        <div className={`flex flex-col w-full lg:w-[30%] h-full ${mobileView === 'preview' ? 'hidden lg:flex' : 'flex'}`}>
          {/* Agent top bar - transparent (hidden on mobile, shown on desktop) */}
          <div className="hidden lg:flex items-center justify-between mb-3 px-3 py-2 glass-header rounded-xl">
            <div className="flex items-center gap-2">
              <label className="text-sm text-light-blue font-medium">Agent</label>
              {/* Custom dropdown with glass style and edit functionality */}
              <AgentDropdown
                agents={agents || []}
                selectedAgentId={agentId || ''}
                onSelect={(selectedAgentId) => {
                  dispatch(resetCreatingErrorMessage());
                  // Clear draft immediately to avoid flashing old content during navigation
                  setAgentInputContent('');
                  setPendingFiles((prevFiles) => {
                    prevFiles.forEach((f) => {
                      try {
                        URL.revokeObjectURL(f.previewUrl);
                      } catch {
                        // ignore
                      }
                    });
                    return [];
                  });
                  navigate(`/agents/${selectedAgentId}`);
                }}
                onUpdate={handleUpdate}
              />
            </div>
            
            {/* Right side: Actions menu */}
            <ActionsMenu
              onRename={() => setIsRenameModalOpen(true)}
              onNewAgent={handleCreateButtonClick}
              onDelete={() => setIsDeleteModalOpen(true)}
              onUpgrade={handleOpenAgentUpgradeModal}
              hasAgents={Boolean(agents && agents.length > 0)}
              isCreatingAgent={isCreating}
            />
          </div>

          {/* Chat area - no glass-surface on mobile for full-screen feel */}
          <div className="flex-1 min-h-0 lg:glass-surface lg:rounded-xl overflow-hidden flex flex-col">
            {agentId ? (
              <MyAgent 
                key={agentId}
                agents={agents} 
                agentId={agentId} 
                isSidebarOpen={false} 
                ref={myAgentRef}
                inputContent={agentInputContent}
                onInputContentChange={setAgentInputContent}
                pendingFiles={pendingFiles}
                onPendingFilesChange={setPendingFiles}
                onSendRequest={handleSendRequest}
                onPreviewUrlChange={undefined}
                onAgentTurnStarted={() => {
                  if (agentId) setBusyAgents(prev => ({ ...prev, [agentId]: true }));
                }}
                onAgentTurnEnded={() => {
                  if (agentId) setBusyAgents(prev => ({ ...prev, [agentId]: false }));
                  setPreviewRefreshTick(t => t + 1);
                }}
                onUpgradePlan={handleAgentUpgradeNow}
                onAttachmentClick={handleAttachmentClick}
                onUpgrade={handleOpenAgentUpgradeModal}
                scrollToBottomOffsetRem={scrollToBottomOffsetRem}
              />
            ) : (creatingErrorMessage ? (
              <div className="grow flex items-center justify-center h-full px-4">
                <div className="glass-surface rounded-xl p-6 max-w-md w-full text-center">
                  <div className="flex items-center justify-center">
                    <ErrorIconSVG />
                  </div>
                  <div className="mt-4 font-sora font-semibold text-base leading-[140%] text-light-grey">
                    {creatingErrorMessage}
                  </div>
                  {creatingErrorMessage === 'Please subscribe to a paying plan in order to create a new Agent.' && (
                    <div className="mt-6 flex items-center justify-center gap-3">
                      <Button type='primary' onClick={handleOpenAgentUpgradeModal}>
                        Upgrade Plan
                      </Button>
                      <Button type='secondary' onClick={() => window.location.reload()}>
                        Reload
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            ) : (isCreating ? (
                <LoadingMessage message="Creating agent..." />
              ) : (
                <GhostMessage>
                  <div className="mt-3 font-inter text-sm text-light-grey/80">
                    Ready to build with AI?
                  </div>
                  <Button type='primary' onClick={handleCreateButtonClick} className="mt-4">
                    + New Agent
                  </Button>
                </GhostMessage>
              )
            ))}
          </div>
        </div>

        {/* Right: Preview section (70% on desktop, full width on mobile when preview is active) */}
        <div className={`w-full lg:w-[70%] h-full ${mobileView === 'chat' ? 'hidden lg:flex' : 'flex'}`}>
            <PreviewErrorBoundary>
              <AppPanel
                key={`${agentId || 'no-agent'}-${dashboardId || 'no-app'}`}  // Force remount when agent OR app changes
                agentId={agentId}
                associatedId={associatedId}
                dashboardId={dashboardId}
                appName={appName}
                agentName={agentName}
                backendHref={backendHref}
                previewUrl={previewUrl}
                publicUrl={publicUrl}
                externalLoading={previewBusy}
                refreshSignal={previewRefreshTick + mobilePreviewRefreshSignal}
                onOpenPaddleCheckout={handleOpenPaddleCheckout}
                onClosePaddleCheckout={handleClosePaddleCheckout}
                isCheckoutOpen={openCheckout}
                isPaddleLoading={isLoadingPaddle}
                paddleError={paddleError}
                paymentCompleted={state.paymentCompleted}
                parseServerURL={parseServerURL}
                parseAppId={parseAppId}
                parseMasterKey={parseMasterKey}
                onPublicFilesChange={setMobilePreviewPublicFiles}
                onCurrentPageChange={setMobilePreviewCurrentPage}
                externalCurrentPage={mobilePreviewCurrentPage}
                publishHandlerRef={publishHandlerRef}
                onPublishStateChange={(disabled, label) => {
                  setMobilePublishDisabled(disabled);
                  setMobilePublishLabel(label);
                }}
                onViewModeChange={setMobileAppViewMode}
                externalViewMode={mobileAppViewMode}
                onClassesChange={setMobileParseClasses}
                onSelectedClassChange={setMobileSelectedClass}
                externalSelectedClass={mobileSelectedClass}
                databaseRefreshHandlerRef={databaseRefreshHandlerRef}
                onDatabaseRefreshingChange={setIsDatabaseRefreshing}
                // On mobile, always force 'functions' view for Cloud Code (no toggle)
                // On desktop, don't pass this prop so it uses its internal state (files by default)
                externalCloudCodeSubView={isMobileScreen ? 'functions' : undefined}
                cloudCodeRefreshHandlerRef={cloudCodeRefreshHandlerRef}
                onCloudCodeRefreshingChange={setIsCloudCodeRefreshing}
                createAppModalOpen={createAppModalOpen}
                onCreateAppModalOpenChange={setCreateAppModalOpen}
                linkAppModalOpen={linkAppModalOpen}
                onLinkAppModalOpenChange={setLinkAppModalOpen}
                createAppPhase={createAppPhase}
                onCreateAppPhaseChange={setCreateAppPhase}
                linkAppPhase={linkAppPhase}
                onLinkAppPhaseChange={setLinkAppPhase}
              />
            </PreviewErrorBoundary>
        </div>
      </div>
      
      {/* FREE credits banner (MOBILE) - must be OUTSIDE the bottom fixed wrapper, but glued and behind it */}
      {mobileView === 'chat' && !!agentId && showFreeCreditsBannerMobile && (
        <div className="lg:hidden fixed bottom-[125px] left-0 right-0 z-40 pointer-events-auto">
          <div className="mb-[20px] glass-input-no-radius glass-input-opaque rounded-t-2xl rounded-b-none !bg-[#222832]">
            <div className="flex items-center justify-between gap-2 pr-2 pl-3 pt-[6px] pb-[20px]">
              <span className="text-xs text-white/70 truncate">
                <span>{remainingFreeMessagesMobile!.toLocaleString()}</span><span> free messages remaining today</span>
              </span>
              <div className="flex items-center gap-1.5 flex-none">
                <button
                  type="button"
                  onClick={handleMobileUpgradeClick}
                  onMouseEnter={() => setIsMobileUpgradeHovered(true)}
                  onMouseLeave={() => setIsMobileUpgradeHovered(false)}
                  className="px-2.5 py-1 rounded-md text-white text-xs font-semibold transition-colors"
                  style={{ backgroundColor: isMobileUpgradeHovered ? '#19875F' : '#27AE60' }}
                >
                  <span>Upgrade Now</span>
                </button>
                <button
                  type="button"
                  onClick={() => setIsMobileCreditsBannerDismissed(true)}
                  className="w-7 h-7 inline-flex items-center justify-center rounded-md text-white/50 hover:text-white hover:bg-white/5 transition-colors"
                  aria-label="Dismiss"
                  title="Dismiss"
                >
                  <CloseIconSVG width="10" height="10" />
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Mobile View Toggle - fixed at bottom, always visible (only on mobile) */}
      {!!agentId && (
      <div className={`lg:hidden fixed bottom-0 left-0 right-0 z-50 !bg-[#19253A] ${mobileView === 'chat' ? 'border-t border-white/10 rounded-t-2xl glass-surface-rounded-none' : ''}`}>
        {/* Input row - only visible when in chat mode */}
        {mobileView === 'chat' && !!agentId && (
          <div className="flex flex-col gap-2 px-4 pt-3 pb-[17px]">
            {/* File previews */}
            {pendingFiles.length > 0 && (
              <div className="flex flex-wrap gap-2 pl-1">
                {pendingFiles.map((pendingFile) => (
                  <div key={pendingFile.id} className="relative group inline-block">
                    <div className="h-10 w-10 rounded-md border border-light-blue/20 overflow-hidden bg-black/20 flex items-center justify-center">
                      {pendingFile.status === 'uploading' ? (
                        <>
                          <img 
                            src={pendingFile.previewUrl} 
                            alt={pendingFile.file.name}
                            className="max-w-full max-h-full object-contain opacity-50"
                          />
                          <div className="absolute inset-0 flex items-center justify-center bg-black/40">
                            <LoadingSpinner width="1rem" height="1rem" />
                          </div>
                        </>
                      ) : (
                        <img 
                          src={pendingFile.previewUrl} 
                          alt={pendingFile.file.name}
                          className="max-w-full max-h-full object-contain"
                        />
                      )}
                    </div>
                    {pendingFile.status !== 'uploading' && (
                      <button
                        onClick={() => removeMobileFile(pendingFile.id)}
                        className="absolute -top-1.5 -right-1.5 w-4 h-4 bg-[#1a2332] border border-white/20 rounded-full flex items-center justify-center text-white/70 hover:text-white transition-colors"
                        title="Remove image"
                      >
                        <CloseIconSVG width="8" height="8" />
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}
            
            {/* Textarea */}
            <textarea
              ref={mobileInputRef}
              placeholder="Ask back4app..."
              className="flex-1 bg-transparent outline-none text-white placeholder:text-light-grey/70 resize-none leading-6 pl-1"
              style={{ maxHeight: '120px' }}
              rows={1}
              value={agentInputContent}
              onChange={(e) => setAgentInputContent(e.target.value)}
              onPaste={handleMobilePaste}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey && !e.altKey && !e.ctrlKey && !e.metaKey) {
                  e.preventDefault();
                  if (!isMobileUploading) {
                    handleMobileSend();
                  }
                }
              }}
              disabled={mobileInputSending || !agentId}
            />
            
            {/* Buttons row */}
            <div className="flex items-center justify-between pl-1">
              {/* Plus button with menu */}
              <div className="relative">
                <button
                  ref={mobileFileButtonRef}
                  onClick={() => setIsMobileFileMenuOpen(!isMobileFileMenuOpen)}
                  className={`flex-none inline-flex items-center justify-center w-8 h-8 rounded-xl transition-all duration-200 ${
                    isMobileFileMenuOpen 
                      ? 'bg-[#34506F] border border-white/20' 
                      : 'bg-white/10 text-light-blue hover:bg-white/15 border border-transparent'
                  }`}
                >
                  <PlusIconSVG width="14" height="14" />
                </button>

                {/* Popover menu with animated opacity */}
                <div 
                  ref={mobileFileMenuRef}
                  className={`absolute bottom-full left-0 mb-2 z-[9999] transition-all duration-200 ease-out origin-bottom-left ${
                    isMobileFileMenuOpen 
                      ? 'opacity-100 scale-100 translate-y-0' 
                      : 'opacity-0 scale-95 translate-y-1 pointer-events-none'
                  }`}
                >
                  <div className="w-48 bg-[#1a2332] border border-white/10 rounded-xl shadow-[0_8px_32px_rgba(0,0,0,0.4)] overflow-hidden">
                    {canTakeScreenshot && (
                      <>
                        <button
                          onClick={handleMobileScreenshot}
                          className="w-full flex items-center gap-3 px-4 py-3 text-sm text-white/80 hover:bg-white/5 transition-colors"
                          type="button"
                        >
                          <ScreenshotIconSVG width="14" height="14" className="text-[#208AEC]" />
                          <span>Take screenshot</span>
                        </button>
                        
                        <div className="h-px bg-white/5 mx-2" />
                      </>
                    )}
                    
                    <button
                      onClick={() => mobileFileInputRef.current?.click()}
                      className="w-full flex items-center gap-3 px-4 py-3 text-sm text-white/80 hover:bg-white/5 transition-colors"
                      type="button"
                    >
                      <AttachIconSVG width="14" height="14" className="text-white/60" />
                      <span>Attach</span>
                    </button>
                  </div>
                </div>
                <input
                  type="file"
                  ref={mobileFileInputRef}
                  className="hidden"
                  accept={AGENT_ATTACHMENT_ALLOWED_TYPES.join(',')}
                  multiple
                  onChange={handleMobileFileSelect}
                />
              </div>
              
              {/* Send/Stop button */}
              {previewBusy && agentId ? (
                // Show stop button when agent is responding (only if agent exists)
                <button
                  onClick={handleMobileStop}
                  disabled={mobileInputStopping}
                  className={`flex-none inline-flex items-center justify-center w-10 h-10 rounded-md transition-colors ${
                    mobileInputStopping 
                      ? 'bg-system-yellow/20 text-system-yellow pointer-events-none opacity-50' 
                      : 'bg-error-red/20 text-error-red hover:bg-error-red/30'
                  }`}
                  aria-label={mobileInputStopping ? "Stopping generation" : "Stop generation"}
                  title={mobileInputStopping ? "Stopping..." : "Stop generation"}
                >
                  {mobileInputStopping ? (
                    <LoadingSpinner width="1.25rem" height="1.25rem" />
                  ) : (
                    <StopIconSVG width="20" height="20" />
                  )}
                </button>
              ) : mobileInputSending || isMobileUploading ? (
                <LoadingSpinner width="2rem" height="2rem" />
              ) : (
                <button
                  onClick={handleMobileSend}
                  disabled={(!agentInputContent.trim() && mobileUploadedFiles.length === 0) || !agentId}
                  className={`flex-none inline-flex items-center justify-center w-10 h-10 rounded-md transition-colors ${
                    (!agentInputContent.trim() && mobileUploadedFiles.length === 0) || !agentId
                      ? 'bg-white/5 text-light-blue/30 cursor-not-allowed' 
                      : 'bg-white/10 text-light-blue hover:bg-white/15'
                  }`}
                  aria-label="Send message"
                >
                  <EnterIconSVG width="20" height="20" />
                </button>
              )}
            </div>
          </div>
        )}
        {/* Mobile contextual controls - only visible in preview mode */}
        {mobileView === 'preview' && (
          <div className="px-4 pt-3">
            {/* Preview URL Bar - when in Preview view mode */}
            {mobileAppViewMode === 'preview' && (
              <MobilePreviewUrlBar
                baseUrl={previewUrl}
                publicFiles={mobilePreviewPublicFiles}
                currentPath={mobilePreviewCurrentPage}
                onPathChange={setMobilePreviewCurrentPage}
                onRefresh={() => setMobilePreviewRefreshSignal(s => s + 1)}
                // Only show "open external" when app is published (publicUrl exists)
                onOpenExternal={publicUrl ? () => {
                  const u = new URL(publicUrl);
                  const rawPath = (mobilePreviewCurrentPage || '/').trim() || '/';
                  u.pathname = rawPath.startsWith('/') ? rawPath : `/${rawPath}`;
                  window.open(u.toString(), '_blank', 'noopener,noreferrer');
                } : undefined}
              />
            )}
            {/* Class Selector Bar - when in Database view mode */}
            {mobileAppViewMode === 'database' && (
              <MobileClassSelectorBar
                classes={mobileParseClasses}
                selectedClass={mobileSelectedClass}
                onClassChange={setMobileSelectedClass}
                onRefresh={() => databaseRefreshHandlerRef.current?.()}
                isRefreshing={isDatabaseRefreshing}
              />
            )}
            {/* Cloud Code Refresh Button - when in Cloud Code view mode */}
            {/* On mobile, we only show Functions view (no toggle), just the refresh button */}
            {mobileAppViewMode === 'cloud-code' && (
              <MobileCloudCodeToggle
                onRefresh={() => cloudCodeRefreshHandlerRef.current?.()}
                isRefreshing={isCloudCodeRefreshing}
              />
            )}
            {/* Deployments don't have contextual controls for now */}
          </div>
        )}
        {/* Toggle buttons + Action buttons - always visible */}
        <div className={`px-4 pb-4 ${mobileView === 'preview' ? 'pt-2' : ''}`}>
          <div className="flex gap-2 items-center">
            {/* Agent Selector button (Chat mode only) */}
            {mobileView === 'chat' && (
              <div className="relative">
                <button
                  ref={mobileAgentSelectorButtonRef}
                  onClick={() => setIsMobileAgentSelectorOpen(!isMobileAgentSelectorOpen)}
                  className={`
                    flex-shrink-0 w-[40px] h-[40px] rounded-xl flex items-center justify-center
                    transition-all duration-200
                    ${isMobileAgentSelectorOpen 
                      ? 'bg-[#34506F] border border-white/20' 
                      : 'bg-[#1a2436] border border-white/10 hover:bg-[#243044]'}
                  `}
                  title="Select Agent"
                  type="button"
                >
                  {/* AI Agent icon (b4a-agent) */}
                  <svg width="20" height="18" viewBox="0 0 22 20" fill="none" className="text-white/70">
                    <path d="M5.71582 13.5889C5.71582 13.9273 5.44191 14.2021 5.10352 14.2021C4.62051 14.2021 4.14132 14.1194 3.6875 13.957L3.49512 13.8818C3.04887 13.697 2.63839 13.437 2.28125 13.1133L2.13184 12.9717C1.79044 12.6303 1.51076 12.2324 1.30469 11.7969L1.2207 11.6074C1.00953 11.0975 0.90039 10.5509 0.90039 9.99902C0.900427 9.51619 0.98421 9.03765 1.14648 8.58398L1.2207 8.39062C1.40557 7.94444 1.66559 7.53385 1.98926 7.17676L2.13184 7.02734C2.4732 6.68604 2.87026 6.40622 3.30566 6.2002L3.49512 6.11621C4.00498 5.90505 4.55165 5.79687 5.10352 5.79687C5.44186 5.79696 5.71573 6.07084 5.71582 6.40918C5.71582 6.7476 5.44191 7.02237 5.10352 7.02246L4.81152 7.03613C4.52065 7.06478 4.23488 7.13676 3.96387 7.24902L3.69922 7.37402C3.44172 7.51175 3.20529 7.68729 2.99805 7.89453L2.80176 8.11133C2.61653 8.33713 2.46471 8.58955 2.35254 8.86035L2.25391 9.13574C2.16923 9.41512 2.12601 9.70604 2.12598 9.99902C2.12598 10.39 2.20295 10.7775 2.35254 11.1387L2.47754 11.4033C2.61527 11.6609 2.79075 11.8972 2.99805 12.1045L3.21484 12.3008C3.44071 12.4861 3.69295 12.6378 3.96387 12.75L4.23926 12.8486C4.51889 12.9334 4.81025 12.9766 5.10352 12.9766C5.44176 12.9766 5.71557 13.2507 5.71582 13.5889Z" fill="currentColor" stroke="currentColor" strokeWidth="0.2" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M6.46387 10.0889L6.36914 10.0605C6.19104 9.98612 6.07399 9.81119 6.07227 9.61816L6.08105 9.52051C6.22861 8.76708 6.89149 8.19847 7.68848 8.19824C8.43579 8.19824 9.06623 8.69753 9.26367 9.38086L9.29688 9.52051C9.34813 9.78307 9.17663 10.0374 8.91406 10.0889L8.81641 10.0977C8.59113 10.0956 8.39069 9.93578 8.3457 9.70605L8.31348 9.59473C8.21663 9.34456 7.97267 9.16699 7.68848 9.16699C7.36391 9.16722 7.09256 9.39902 7.03223 9.70605L7.00391 9.80078C6.9171 10.0086 6.69347 10.1336 6.46387 10.0889Z" fill="currentColor" stroke="currentColor" strokeWidth="0.2" strokeLinecap="round"/>
                    <path d="M13.6426 10.0889L13.5479 10.0605C13.3697 9.98612 13.2527 9.81119 13.251 9.61816L13.2598 9.52051C13.4073 8.76708 14.0702 8.19847 14.8672 8.19824C15.6145 8.19824 16.2449 8.69753 16.4424 9.38086L16.4756 9.52051C16.5268 9.78307 16.3553 10.0374 16.0928 10.0889L15.9951 10.0977C15.7698 10.0956 15.5694 9.93578 15.5244 9.70605L15.4922 9.59473C15.3953 9.34456 15.1514 9.16699 14.8672 9.16699C14.5426 9.16722 14.2713 9.39902 14.2109 9.70605L14.1826 9.80078C14.0958 10.0086 13.8722 10.1336 13.6426 10.0889Z" fill="currentColor" stroke="currentColor" strokeWidth="0.2" strokeLinecap="round"/>
                    <path d="M16.2852 6.41113C16.2852 6.07272 16.5591 5.79794 16.8975 5.79785C17.3805 5.79785 17.8597 5.88062 18.3135 6.04297L18.5059 6.11816C18.9521 6.30301 19.3626 6.56305 19.7197 6.88672L19.8691 7.02832C20.2105 7.36972 20.4902 7.76764 20.6963 8.20313L20.7803 8.39258C20.9914 8.90245 21.1006 9.4491 21.1006 10.001C21.1005 10.4838 21.0168 10.9624 20.8545 11.416L20.7803 11.6094C20.5954 12.0556 20.3354 12.4662 20.0117 12.8232L19.8691 12.9727C19.5278 13.314 19.1307 13.5938 18.6953 13.7998L18.5059 13.8838C17.996 14.095 17.4493 14.2031 16.8975 14.2031C16.5591 14.203 16.2852 13.9292 16.2852 13.5908C16.2852 13.2524 16.5591 12.9776 16.8975 12.9775L17.1895 12.9639C17.4803 12.9352 17.7661 12.8632 18.0371 12.751L18.3018 12.626C18.5593 12.4883 18.7957 12.3127 19.0029 12.1055L19.1992 11.8887C19.3844 11.6629 19.5363 11.4105 19.6484 11.1396L19.7471 10.8643C19.8317 10.5849 19.875 10.294 19.875 10.001C19.875 9.61004 19.798 9.22251 19.6484 8.86133L19.5234 8.59668C19.3857 8.33908 19.2102 8.10282 19.0029 7.89551L18.7861 7.69922C18.5603 7.51388 18.308 7.36223 18.0371 7.25L17.7617 7.15137C17.4821 7.06656 17.1907 7.02344 16.8975 7.02344C16.5592 7.02335 16.2854 6.74933 16.2852 6.41113Z" fill="currentColor" stroke="currentColor" strokeWidth="0.2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </button>

                {/* Agent selector dropdown - opens up and to the right */}
                {isMobileAgentSelectorOpen && (
                  <div
                    ref={mobileAgentSelectorMenuRef}
                    className="absolute bottom-full left-0 mb-2 z-[9999]"
                  >
                    <div className="w-56 bg-[#1a2332] border border-white/10 rounded-xl shadow-[0_8px_32px_rgba(0,0,0,0.4)] overflow-hidden">
                      {agents && agents.length > 0 ? (
                        <div className="max-h-[240px] overflow-y-auto glass-scroll">
                          {agents.map((agent) => (
                            <button
                              key={agent.id}
                              onClick={() => {
                                setIsMobileAgentSelectorOpen(false);
                                if (agent.id !== agentId) {
                                  navigate(`/agents/${agent.id}`);
                                }
                              }}
                              className={`
                                w-full flex items-center gap-3 px-4 py-3 text-left
                                transition-colors duration-150
                                ${agent.id === agentId
                                  ? 'bg-[#208AEC]/20 text-white font-medium'
                                  : 'text-white/90 hover:bg-white/5'
                                }
                              `}
                              type="button"
                            >
                              {agent.id === agentId && (
                                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" className="text-[#208AEC] flex-shrink-0">
                                  <path d="M13.3333 4L6 11.3333L2.66667 8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                </svg>
                              )}
                              <span className="truncate"><span>{agent.name}</span></span>
                            </button>
                          ))}
                        </div>
                      ) : (
                        /* Empty state - no agents */
                        <div className="flex items-center gap-3 p-4">
                          <span className="text-white/60 text-sm flex-1">No agents found</span>
                          <button
                            onClick={() => {
                              setIsMobileAgentSelectorOpen(false);
                              handleCreateButtonClick();
                            }}
                            className="flex-shrink-0 w-[25px] h-[25px] rounded-lg bg-[#22c55e] hover:bg-[#16a34a] flex items-center justify-center transition-colors"
                            style={{backgroundColor: '#22c55e'}}
                            type="button"
                            title="Create new agent"
                          >
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" className="text-white">
                              <path d="M12 5V19M5 12H19" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}
            {/* View Mode Selector button (Preview mode only) */}
            {mobileView === 'preview' && (
              <div className="relative">
                <button
                  ref={mobileViewModeSelectorButtonRef}
                  onClick={() => setIsMobileViewModeSelectorOpen(!isMobileViewModeSelectorOpen)}
                  className={`
                    flex-shrink-0 w-[40px] h-[40px] rounded-xl flex items-center justify-center
                    transition-all duration-200
                    ${isMobileViewModeSelectorOpen 
                      ? 'bg-[#34506F] border border-white/20' 
                      : 'bg-[#1a2436] border border-white/10 hover:bg-[#243044]'}
                  `}
                  style={{ backgroundColor: isMobileViewModeSelectorOpen ? '#34506F' : '#1a2436' }}
                  title={VIEW_MODE_OPTIONS.find(o => o.id === mobileAppViewMode)?.label || 'Preview'}
                  type="button"
                >
                  {/* Dynamic icon based on current view mode */}
                  {mobileAppViewMode === 'preview' && (
                    <svg width="18" height="18" viewBox="0 0 26 26" fill="none" className="text-[#208AEC]">
                      <rect x="2" y="4" width="22" height="16" rx="2" stroke="currentColor" strokeWidth="2"/>
                      <path d="M9 23H17" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                      <path d="M13 20V23" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                      <circle cx="13" cy="12" r="3" stroke="currentColor" strokeWidth="2"/>
                    </svg>
                  )}
                  {mobileAppViewMode === 'database' && (
                    <svg width="18" height="18" viewBox="0 0 26 26" fill="none" className="text-[#208AEC]">
                      <ellipse cx="13" cy="6" rx="9" ry="3" stroke="currentColor" strokeWidth="2"/>
                      <path d="M4 6V13C4 14.6569 8.02944 16 13 16C17.9706 16 22 14.6569 22 13V6" stroke="currentColor" strokeWidth="2"/>
                      <path d="M4 13V20C4 21.6569 8.02944 23 13 23C17.9706 23 22 21.6569 22 20V13" stroke="currentColor" strokeWidth="2"/>
                    </svg>
                  )}
                  {mobileAppViewMode === 'cloud-code' && (
                    <svg width="18" height="18" viewBox="0 0 26 26" fill="none" className="text-[#208AEC]">
                      <path d="M8 9L4 13L8 17" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M18 9L22 13L18 17" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M15 5L11 21" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                    </svg>
                  )}
                  {mobileAppViewMode === 'deployments' && (
                    <svg width="18" height="18" viewBox="0 0 26 26" fill="none" className="text-[#208AEC]">
                      <path fillRule="evenodd" clipRule="evenodd" d="M3.37475 12.1306L5.78933 13.0965L7.85534 8.96444L6.70524 8.80014L3.37475 12.1306ZM5.87453 15.1732L1.33514 13.3574C0.70305 13.1046 0.535403 12.2878 1.01679 11.8065L5.69944 7.12381C5.91049 6.91275 6.2086 6.81338 6.50408 6.85559L9.426 7.27301C9.44003 7.27501 9.45392 7.27731 9.46768 7.27988C10.5285 5.63824 11.9157 4.22102 13.5522 3.12136C15.7374 1.65295 18.3217 0.892833 20.954 0.944267L23.5729 0.99544C24.1018 1.00578 24.6061 1.22046 24.9801 1.59448C25.3662 1.98063 25.582 2.5051 25.5795 3.0512L25.5687 5.34249C25.5561 8.05024 24.7019 10.6871 23.1244 12.8879C22.0001 14.4565 20.5833 15.7828 18.9595 16.7984L18.9609 16.8079L19.3783 19.7299C19.4206 20.0253 19.3212 20.3234 19.1101 20.5345L14.4275 25.2171C13.9461 25.6985 13.1293 25.5309 12.8765 24.8988L11.0608 20.3594L11.0115 20.3804C10.1441 20.0172 9.3429 19.5325 8.62844 18.9468L8.04653 19.5287C7.6762 19.899 7.07578 19.899 6.70546 19.5287C6.33513 19.1584 6.33513 18.558 6.70546 18.1876L7.28735 17.6057C6.70158 16.8912 6.21676 16.0899 5.85352 15.2225L5.87453 15.1732ZM8.63661 16.2565C8.3737 15.9194 8.13814 15.5596 7.93309 15.1804L10.0537 10.2047C11.0077 7.96631 12.5904 6.05263 14.61 4.69553C16.472 3.44432 18.674 2.79664 20.9169 2.84046L23.5359 2.89164C23.5746 2.89239 23.6116 2.90813 23.639 2.93555C23.6673 2.96385 23.6831 3.00229 23.6829 3.04232L23.6722 5.33361C23.6614 7.64805 22.9312 9.90187 21.5829 11.783C20.2124 13.6951 18.3431 15.1941 16.1789 16.1165L11.0536 18.3009C10.6744 18.0959 10.3147 17.8604 9.9777 17.5975L12.7403 14.835C13.1106 14.4646 13.1106 13.8642 12.7403 13.4939C12.3699 13.1236 11.7695 13.1236 11.3992 13.4939L8.63661 16.2565ZM17.7053 8.67052C18.0757 9.04084 18.0757 9.64126 17.7053 10.0116L17.0116 10.7053C16.6413 11.0756 16.0409 11.0756 15.6705 10.7053C15.3002 10.335 15.3002 9.73458 15.6705 9.36426L16.3643 8.67052C16.7346 8.30019 17.335 8.30019 17.7053 8.67052ZM13.1375 20.4446L14.1033 22.8592L17.4338 19.5287L17.2695 18.3786L13.1375 20.4446Z" fill="currentColor"/>
                    </svg>
                  )}
                </button>

                {/* View mode selector dropdown - opens up and to the right */}
                {isMobileViewModeSelectorOpen && (
                  <div
                    ref={mobileViewModeSelectorMenuRef}
                    className="absolute bottom-full left-0 mb-2 z-[9999]"
                  >
                    <div className="w-48 bg-[#1a2332] border border-white/10 rounded-xl shadow-[0_8px_32px_rgba(0,0,0,0.4)] overflow-hidden">
                      {VIEW_MODE_OPTIONS.map((option) => {
                        const isSelected = option.id === mobileAppViewMode;
                        return (
                          <button
                            key={option.id}
                            onClick={() => {
                              setIsMobileViewModeSelectorOpen(false);
                              setMobileAppViewMode(option.id);
                            }}
                            className={`
                              w-full flex items-center gap-3 px-4 py-3 text-left
                              transition-colors duration-150
                              ${isSelected
                                ? 'bg-[#208AEC]/20 text-white font-medium'
                                : 'text-white/90 hover:bg-white/5'
                              }
                            `}
                            type="button"
                          >
                            {/* Icon for each option */}
                            {option.id === 'preview' && (
                              <svg width="18" height="18" viewBox="0 0 26 26" fill="none" className={isSelected ? 'text-[#208AEC]' : 'text-white/50'}>
                                <rect x="2" y="4" width="22" height="16" rx="2" stroke="currentColor" strokeWidth="2"/>
                                <path d="M9 23H17" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                                <path d="M13 20V23" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                                <circle cx="13" cy="12" r="3" stroke="currentColor" strokeWidth="2"/>
                              </svg>
                            )}
                            {option.id === 'database' && (
                              <svg width="18" height="18" viewBox="0 0 26 26" fill="none" className={isSelected ? 'text-[#208AEC]' : 'text-white/50'}>
                                <ellipse cx="13" cy="6" rx="9" ry="3" stroke="currentColor" strokeWidth="2"/>
                                <path d="M4 6V13C4 14.6569 8.02944 16 13 16C17.9706 16 22 14.6569 22 13V6" stroke="currentColor" strokeWidth="2"/>
                                <path d="M4 13V20C4 21.6569 8.02944 23 13 23C17.9706 23 22 21.6569 22 20V13" stroke="currentColor" strokeWidth="2"/>
                              </svg>
                            )}
                            {option.id === 'cloud-code' && (
                              <svg width="18" height="18" viewBox="0 0 26 26" fill="none" className={isSelected ? 'text-[#208AEC]' : 'text-white/50'}>
                                <path d="M8 9L4 13L8 17" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                <path d="M18 9L22 13L18 17" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                <path d="M15 5L11 21" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                              </svg>
                            )}
                            {option.id === 'deployments' && (
                              <svg width="18" height="18" viewBox="0 0 26 26" fill="none" className={isSelected ? 'text-[#208AEC]' : 'text-white/50'}>
                                <path fillRule="evenodd" clipRule="evenodd" d="M3.37475 12.1306L5.78933 13.0965L7.85534 8.96444L6.70524 8.80014L3.37475 12.1306ZM5.87453 15.1732L1.33514 13.3574C0.70305 13.1046 0.535403 12.2878 1.01679 11.8065L5.69944 7.12381C5.91049 6.91275 6.2086 6.81338 6.50408 6.85559L9.426 7.27301C9.44003 7.27501 9.45392 7.27731 9.46768 7.27988C10.5285 5.63824 11.9157 4.22102 13.5522 3.12136C15.7374 1.65295 18.3217 0.892833 20.954 0.944267L23.5729 0.99544C24.1018 1.00578 24.6061 1.22046 24.9801 1.59448C25.3662 1.98063 25.582 2.5051 25.5795 3.0512L25.5687 5.34249C25.5561 8.05024 24.7019 10.6871 23.1244 12.8879C22.0001 14.4565 20.5833 15.7828 18.9595 16.7984L18.9609 16.8079L19.3783 19.7299C19.4206 20.0253 19.3212 20.3234 19.1101 20.5345L14.4275 25.2171C13.9461 25.6985 13.1293 25.5309 12.8765 24.8988L11.0608 20.3594L11.0115 20.3804C10.1441 20.0172 9.3429 19.5325 8.62844 18.9468L8.04653 19.5287C7.6762 19.899 7.07578 19.899 6.70546 19.5287C6.33513 19.1584 6.33513 18.558 6.70546 18.1876L7.28735 17.6057C6.70158 16.8912 6.21676 16.0899 5.85352 15.2225L5.87453 15.1732ZM8.63661 16.2565C8.3737 15.9194 8.13814 15.5596 7.93309 15.1804L10.0537 10.2047C11.0077 7.96631 12.5904 6.05263 14.61 4.69553C16.472 3.44432 18.674 2.79664 20.9169 2.84046L23.5359 2.89164C23.5746 2.89239 23.6116 2.90813 23.639 2.93555C23.6673 2.96385 23.6831 3.00229 23.6829 3.04232L23.6722 5.33361C23.6614 7.64805 22.9312 9.90187 21.5829 11.783C20.2124 13.6951 18.3431 15.1941 16.1789 16.1165L11.0536 18.3009C10.6744 18.0959 10.3147 17.8604 9.9777 17.5975L12.7403 14.835C13.1106 14.4646 13.1106 13.8642 12.7403 13.4939C12.3699 13.1236 11.7695 13.1236 11.3992 13.4939L8.63661 16.2565ZM17.7053 8.67052C18.0757 9.04084 18.0757 9.64126 17.7053 10.0116L17.0116 10.7053C16.6413 11.0756 16.0409 11.0756 15.6705 10.7053C15.3002 10.335 15.3002 9.73458 15.6705 9.36426L16.3643 8.67052C16.7346 8.30019 17.335 8.30019 17.7053 8.67052ZM13.1375 20.4446L14.1033 22.8592L17.4338 19.5287L17.2695 18.3786L13.1375 20.4446Z" fill="currentColor"/>
                              </svg>
                            )}
                            <span className="font-medium">{option.label}</span>
                            {isSelected && (
                              <svg className="ml-auto text-[#208AEC]" width="16" height="16" viewBox="0 0 16 16" fill="none">
                                <path d="M3 8L6.5 11.5L13 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                              </svg>
                            )}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            )}
            {/* Chat/Preview toggle */}
            <div className="flex-1 flex gap-1 p-1 bg-[#1a2436] rounded-full border border-white/10">
              <button
                onClick={() => setMobileView('chat')}
                className={`
                  flex-1 h-[32px] rounded-full font-medium text-sm transition-all duration-200 flex items-center justify-center
                  ${mobileView === 'chat'
                    ? 'bg-[#34506F] text-white'
                    : 'bg-transparent text-white/60 hover:text-white/80'
                  }
                `}
              >
                <span>Chat</span>
              </button>
              <button
                onClick={() => setMobileView('preview')}
                className={`
                  flex-1 h-[32px] rounded-full font-medium text-sm transition-all duration-200 flex items-center justify-center
                  ${mobileView === 'preview'
                    ? 'bg-[#34506F] text-white'
                    : 'bg-transparent text-white/60 hover:text-white/80'
                  }
                `}
              >
                <span>{VIEW_MODE_OPTIONS.find(o => o.id === mobileAppViewMode)?.label || 'Preview'}</span>
              </button>
            </div>
            {/* Actions button (Chat mode) or Publish button (Preview mode) */}
            {mobileView === 'chat' ? (
              <div className="relative">
                <button
                  ref={mobileActionsButtonRef}
                  onClick={() => setIsMobileActionsOpen(!isMobileActionsOpen)}
                  className={`
                    flex-shrink-0 w-[40px] h-[40px] rounded-xl flex items-center justify-center
                    transition-all duration-200
                    ${isMobileActionsOpen 
                      ? 'bg-[#34506F] border border-white/20' 
                      : 'bg-[#1a2436] border border-white/10 hover:bg-[#243044]'}
                  `}
                  title="More actions"
                  type="button"
                >
                  {/* Three dots icon */}
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" className="text-white/70">
                    <circle cx="12" cy="5" r="2" />
                    <circle cx="12" cy="12" r="2" />
                    <circle cx="12" cy="19" r="2" />
                  </svg>
                </button>

                {/* Actions dropdown - opens up and to the left */}
                {isMobileActionsOpen && (
                  <div
                    ref={mobileActionsMenuRef}
                    className="absolute bottom-full right-0 mb-2 z-[9999]"
                  >
                    <div className="w-[280px] bg-[#1a2332] border border-white/10 rounded-xl shadow-[0_8px_32px_rgba(0,0,0,0.4)] overflow-hidden">
                      {/* Usage Card - inline subscription info */}
                      <UsageCard isActive={isMobileActionsOpen} onUpgrade={() => { setIsMobileActionsOpen(false); handleOpenAgentUpgradeModal(); }} />

                      {/* Divider */}
                      <div className="h-px bg-white/5 mx-2" />

                      {/* Rename */}
                      <button
                        onClick={() => {
                          setIsMobileActionsOpen(false);
                          setIsRenameModalOpen(true);
                        }}
                        className="w-full flex items-center gap-3 px-4 py-3 text-sm text-white/80 hover:bg-white/5 transition-colors"
                        type="button"
                      >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-white/60">
                          <path d="M17 3a2.828 2.828 0 114 4L7.5 20.5 2 22l1.5-5.5L17 3z" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                        <span>Rename</span>
                      </button>

                      {/* New Agent */}
                      <button
                        onClick={() => {
                          setIsMobileActionsOpen(false);
                          handleCreateButtonClick();
                        }}
                        disabled={isCreating}
                        className="w-full flex items-center gap-3 px-4 py-3 text-sm text-white/80 hover:bg-white/5 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        type="button"
                      >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-white/60">
                          <path d="M12 5v14M5 12h14" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                        <span>New Agent</span>
                      </button>

                      {/* Divider */}
                      <div className="h-px bg-white/5 mx-2" />

                      {/* Delete */}
                      <button
                        onClick={() => {
                          setIsMobileActionsOpen(false);
                          setIsDeleteModalOpen(true);
                        }}
                        className="w-full flex items-center gap-3 px-4 py-3 text-sm text-red-400 hover:bg-red-500/10 transition-colors"
                        type="button"
                      >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M3 6h18M8 6V4a2 2 0 012-2h4a2 2 0 012 2v2m3 0v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6h14z" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                        <span>Delete</span>
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <button
                onClick={() => publishHandlerRef.current?.()}
                disabled={mobilePublishDisabled && mobilePublishLabel !== 'Domain Settings'}
                className={`
                  flex-shrink-0 w-[40px] h-[40px] rounded-xl flex items-center justify-center
                  transition-all duration-200
                  ${mobilePublishDisabled && mobilePublishLabel !== 'Domain Settings'
                    ? 'bg-white/5 text-white/40 cursor-not-allowed border border-white/10'
                    : 'bg-gradient-to-r from-[#22c55e] to-[#16a34a] text-white hover:from-[#16a34a] hover:to-[#15803d] shadow-lg shadow-[#22c55e]/20 hover:shadow-[#22c55e]/30 border border-[#22c55e]/30'
                  }
                `}
                title={mobilePublishLabel}
              >
                {mobilePublishLabel === 'Domain Settings' ? (
                  /* Gear/Settings icon */
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="3" />
                    <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z" />
                  </svg>
                ) : (
                  /* Upload icon */
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                    <polyline points="17 8 12 3 7 8" />
                    <line x1="12" y1="3" x2="12" y2="15" />
                  </svg>
                )}
              </button>
            )}
          </div>
        </div>
      </div>
      )}

      {/* Usage Modal */}
      <UsageModal
        open={isUsageModalOpen}
        onClose={() => setIsUsageModalOpen(false)}
      />

      {/* Rename Agent Modal */}
      <RenameAgentModal
        open={isRenameModalOpen}
        onClose={() => setIsRenameModalOpen(false)}
        currentName={agents?.find(a => a.id === agentId)?.name || ''}
        onRename={async (newName) => {
          if (agentId) {
            await handleUpdate(agentId, newName);
          }
        }}
      />

      {/* Delete Agent Modal */}
      <DeleteAgentModal
        open={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        agentName={agents?.find(a => a.id === agentId)?.name || ''}
        onDelete={async () => {
          if (agentId) {
            await handleDelete(agentId);
          }
        }}
      />
      <AgentUpgradeModal
        appId={billingAgentId}
        isOpen={isAgentUpgradeModalOpen}
        onClose={handleAgentUpgradeClose}
      />

      {/* Attachment Preview Modal */}
      {selectedAttachment && (
        <AttachmentPreviewModal
          attachment={selectedAttachment}
          onClose={handleAttachmentModalClose}
        />
      )}
    </div>
    </>
  );
}

export default MyAgents;